import React, {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { InputAdornment, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { KEY_CODES } from 'constants/keyboard';
import { COLOR_PALETTE } from 'constants/ui';
import useFocusSearchOnType from 'hooks/useFocusSearchOnType';
import { getConditionalObject } from 'utils/data';

import TextFieldCDS from '../form/TextFieldCDS';
import { CancelIcon, SearchIcon } from '../icons';

import { DEFAULT_SEARCH_INPUT_NAME, FORBIDDEN_SEARCH_CHARS } from './SearchInputCDS.constants';

const defaultObject = {};

const SearchInputCDS = forwardRef(
  (
    {
      searchOnType = false,
      focusOnType = false,
      onSearch,
      InputProps = defaultObject,
      validQueryLength = 3,
      disabled,
      name = DEFAULT_SEARCH_INPUT_NAME,
      ...props
    },
    ref,
  ) => {
    const internalInputRef = useRef(null);

    useImperativeHandle(ref, () => ({ focus: () => internalInputRef.current?.focus() }), []);

    const [searchValue, setSearchValue] = useState('');
    const [isSearchPerformed, setIsSearchPerformed] = useState(true);

    const isValidQueryLength = useCallback(
      query => !query.length || query?.trim().length >= validQueryLength,
      [validQueryLength],
    );

    const onChange = useCallback(
      ({ target }) => {
        const { value } = target;

        if (value.match(FORBIDDEN_SEARCH_CHARS)) {
          return;
        }

        setSearchValue(value);
        setIsSearchPerformed(searchOnType);

        if (searchOnType) {
          onSearch(value);
        }
      },
      [searchOnType, onSearch],
    );

    const onEnterPress = useCallback(
      ({ target: { value }, charCode }) => {
        if (charCode === KEY_CODES.ENTER && (!value.length || isValidQueryLength(value))) {
          onSearch(value.toLowerCase());
          setIsSearchPerformed(true);
        }
      },
      [isValidQueryLength, onSearch],
    );

    const onClear = useCallback(() => {
      setSearchValue('');
      onSearch('');
    }, [onSearch]);

    const onType = useCallback(
      char => {
        if (disabled || char.match(FORBIDDEN_SEARCH_CHARS)) {
          return;
        }

        setSearchValue(char);
        setIsSearchPerformed(searchOnType);

        if (searchOnType) {
          onSearch(char);
        }
      },
      [disabled, searchOnType, onSearch],
    );

    useFocusSearchOnType({ enabled: focusOnType && !disabled, inputRef: internalInputRef, onType });

    const inputProps = useMemo(
      () => ({
        ...InputProps,
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon color={COLOR_PALETTE.GRAY[7]} size={IconSize[20]} />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            {!isSearchPerformed && isValidQueryLength(searchValue) && !searchOnType ? (
              <Typography color="text.secondary" variant="regular14">
                Press &quot;Enter&quot;
              </Typography>
            ) : null}
            <CancelIcon
              className={`mr-8 ${disabled ? '' : 'cursor-pointer'}`}
              color={COLOR_PALETTE.GRAY[7]}
              size={IconSize[20]}
              {...getConditionalObject(!disabled, { onClick: onClear })}
            />
          </InputAdornment>
        ),
      }),
      [
        InputProps,
        disabled,
        isSearchPerformed,
        isValidQueryLength,
        searchValue,
        searchOnType,
        onClear,
      ],
    );

    return (
      <TextFieldCDS
        {...props}
        InputProps={inputProps}
        disabled={disabled}
        inputRef={internalInputRef}
        name={name}
        value={searchValue}
        onChange={onChange}
        onKeyPress={!searchOnType ? onEnterPress : undefined}
      />
    );
  },
);

SearchInputCDS.propTypes = {
  onSearch: PropTypes.func.isRequired,
  name: PropTypes.string,
  InputProps: PropTypes.object,
  searchOnType: PropTypes.bool,
  focusOnType: PropTypes.bool,
  validQueryLength: PropTypes.number,
  disabled: PropTypes.bool,
  dataTestId: PropTypes.string,
};

export default memo(SearchInputCDS);
